/**
 * Word ladders: change one letter at a time, every step a real word.
 *
 * The dictionary is ENABLE, the same public-domain word-game list the Anagram Solver
 * uses (`public/data/enable-words.txt`, lowercase, one word per line). It is fetched once
 * per page, on first use, and never leaves the browser.
 *
 * Neighbours are found through "wildcard buckets": the word `cold` goes into the buckets
 * `*old`, `c*ld`, `co*d` and `col*`, and two words that differ in exactly one letter share
 * exactly one bucket. A breadth-first search from the start word then gives the shortest
 * ladder, and counting how many ways each word is reached gives the number of different
 * shortest ladders.
 */

import {
  EVERYDAY_WORDS,
  hasEverydayWords,
} from "@/lib/tool-kit/games-organizers/word-ladder-everyday";
import { type Rng, randInt, shuffled } from "@/lib/tool-kit/worksheets/random";

export const MIN_LADDER_LENGTH = 2;
export const MAX_LADDER_LENGTH = 8;
/** Counts above this are shown as "more than"; they only need to be big. */
export const PATH_COUNT_CAP = 1_000_000;

const LETTERS_ONLY = /^[a-z]+$/;
const SPLIT = /[\s,;]+/;

export interface LadderIndex {
  length: number;
  words: Set<string>;
  buckets: Map<string, string[]>;
}

/** Index the words of one length. */
export function buildLadderIndex(
  allWords: readonly string[],
  length: number
): LadderIndex {
  const words = new Set<string>();
  const buckets = new Map<string, string[]>();
  for (const word of allWords) {
    if (word.length !== length) {
      continue;
    }
    words.add(word);
    for (let i = 0; i < length; i += 1) {
      const key = `${word.slice(0, i)}*${word.slice(i + 1)}`;
      const list = buckets.get(key);
      if (list) {
        list.push(word);
      } else {
        buckets.set(key, [word]);
      }
    }
  }
  return { length, words, buckets };
}

const indexCache = new Map<number, LadderIndex>();

/** The index for a word length, built once from the full list. */
export function ladderIndexFor(
  allWords: readonly string[],
  length: number
): LadderIndex {
  const cached = indexCache.get(length);
  if (cached) {
    return cached;
  }
  const built = buildLadderIndex(allWords, length);
  indexCache.set(length, built);
  return built;
}

/** Words one letter away from `word`, in the list and not banned. */
export function neighbours(
  index: LadderIndex,
  word: string,
  banned: ReadonlySet<string> = new Set()
): string[] {
  const found: string[] = [];
  for (let i = 0; i < word.length; i += 1) {
    const key = `${word.slice(0, i)}*${word.slice(i + 1)}`;
    for (const other of index.buckets.get(key) ?? []) {
      if (other !== word && !banned.has(other)) {
        found.push(other);
      }
    }
  }
  return found;
}

/** The position at which two same-length words differ, or -1 unless it is exactly one. */
export function changedPosition(a: string, b: string): number {
  if (a.length !== b.length) {
    return -1;
  }
  let position = -1;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) {
      if (position !== -1) {
        return -1;
      }
      position = i;
    }
  }
  return position;
}

export interface Ladder {
  /** Start, every rung, and end. */
  words: string[];
  /** Number of one-letter changes: `words.length − 1`. */
  steps: number;
  /** The position that changes at each step. */
  changes: number[];
}

export interface ShortestLadder extends Ladder {
  /** How many different ladders have this length, capped at `PATH_COUNT_CAP`. */
  count: number;
}

function toLadder(words: string[]): Ladder {
  return {
    words,
    steps: words.length - 1,
    changes: words
      .slice(1)
      .map((word, i) => changedPosition(words[i] as string, word)),
  };
}

function walkBack(
  parent: ReadonlyMap<string, string>,
  start: string,
  end: string
): string[] {
  const words = [end];
  let word = end;
  while (word !== start) {
    word = parent.get(word) as string;
    words.push(word);
  }
  return words.reverse();
}

interface Walk {
  distance: Map<string, number>;
  counts: Map<string, number>;
  parent: Map<string, string>;
  queue: string[];
}

/** Visit one word's neighbours in a breadth-first search that also counts shortest routes. */
function expand(
  walk: Walk,
  index: LadderIndex,
  word: string,
  banned: ReadonlySet<string>
): void {
  const here = walk.distance.get(word) ?? 0;
  for (const next of neighbours(index, word, banned)) {
    const known = walk.distance.get(next);
    if (known === undefined) {
      walk.distance.set(next, here + 1);
      walk.parent.set(next, word);
      walk.counts.set(next, walk.counts.get(word) ?? 0);
      walk.queue.push(next);
    } else if (known === here + 1) {
      const total = (walk.counts.get(next) ?? 0) + (walk.counts.get(word) ?? 0);
      walk.counts.set(next, Math.min(PATH_COUNT_CAP, total));
    }
  }
}

/** The shortest ladder from `start` to `end`, or null when the words are not connected. */
export function shortestLadder(
  index: LadderIndex,
  start: string,
  end: string,
  banned: ReadonlySet<string> = new Set()
): ShortestLadder | null {
  if (!(index.words.has(start) && index.words.has(end))) {
    return null;
  }
  const walk: Walk = {
    distance: new Map([[start, 0]]),
    counts: new Map([[start, 1]]),
    parent: new Map(),
    queue: [start],
  };
  for (const word of walk.queue) {
    const reached = walk.distance.get(end);
    if (reached !== undefined && (walk.distance.get(word) ?? 0) >= reached) {
      break;
    }
    expand(walk, index, word, banned);
  }
  if (!walk.distance.has(end)) {
    return null;
  }
  return {
    ...toLadder(walkBack(walk.parent, start, end)),
    count: walk.counts.get(end) ?? 1,
  };
}

/** What stepping onto a word outside the everyday list costs, against 1 for an everyday word. */
export const UNFAMILIAR_COST = 4;

interface Dial {
  cost: Map<string, number>;
  parent: Map<string, string>;
  buckets: string[][];
}

/** Relax every edge out of `word` in Dial's algorithm. */
function relax(
  dial: Dial,
  index: LadderIndex,
  familiar: (word: string) => boolean,
  word: string,
  level: number,
  banned: ReadonlySet<string>
): void {
  for (const next of neighbours(index, word, banned)) {
    const total = level + (familiar(next) ? 1 : UNFAMILIAR_COST);
    if (total < (dial.cost.get(next) ?? Number.POSITIVE_INFINITY)) {
      dial.cost.set(next, total);
      dial.parent.set(next, word);
      dial.buckets[total] = [...(dial.buckets[total] ?? []), next];
    }
  }
}

/**
 * The ladder that leans on everyday words: each step onto an everyday word costs 1 and
 * each step onto any other ENABLE word costs `UNFAMILIAR_COST`, so a ladder takes up to
 * three extra everyday steps to avoid one unfamiliar word. Found with Dial's algorithm
 * (Dijkstra with integer costs, one bucket per total cost).
 */
export function everydayLadder(
  index: LadderIndex,
  everyday: ReadonlySet<string>,
  start: string,
  end: string,
  banned: ReadonlySet<string> = new Set()
): Ladder | null {
  if (!(index.words.has(start) && index.words.has(end))) {
    return null;
  }
  const dial: Dial = {
    cost: new Map([[start, 0]]),
    parent: new Map(),
    buckets: [[start]],
  };
  const familiar = (word: string) => word === end || everyday.has(word);
  for (let level = 0; level < dial.buckets.length; level += 1) {
    for (const word of dial.buckets[level] ?? []) {
      if (dial.cost.get(word) === level) {
        if (word === end) {
          return toLadder(walkBack(dial.parent, start, end));
        }
        relax(dial, index, familiar, word, level, banned);
      }
    }
  }
  return null;
}

export type InputProblem =
  | "empty"
  | "letters"
  | "length"
  | "same"
  | "too-short"
  | "too-long"
  | "unknown-start"
  | "unknown-end";

export interface CheckedInput {
  start: string;
  end: string;
  problem: InputProblem | null;
}

/** Normalise and validate a start/end pair, optionally against an index. */
export function checkEnds(
  rawStart: string,
  rawEnd: string,
  index: LadderIndex | null
): CheckedInput {
  const start = rawStart.trim().toLowerCase();
  const end = rawEnd.trim().toLowerCase();
  const result = (problem: InputProblem | null): CheckedInput => ({
    start,
    end,
    problem,
  });
  if (!(start && end)) {
    return result("empty");
  }
  if (!(LETTERS_ONLY.test(start) && LETTERS_ONLY.test(end))) {
    return result("letters");
  }
  if (start.length !== end.length) {
    return result("length");
  }
  if (start.length < MIN_LADDER_LENGTH) {
    return result("too-short");
  }
  if (start.length > MAX_LADDER_LENGTH) {
    return result("too-long");
  }
  if (start === end) {
    return result("same");
  }
  if (index && !index.words.has(start)) {
    return result("unknown-start");
  }
  if (index && !index.words.has(end)) {
    return result("unknown-end");
  }
  return result(null);
}

/** Words typed as a list: split on spaces, commas, semicolons and line breaks. */
export function parseWordInput(text: string): string[] {
  return text
    .split(SPLIT)
    .map((word) => word.trim().toLowerCase())
    .filter((word) => word.length > 0);
}

export type StepStatus = "ok" | "not-a-word" | "bad-change" | "repeated";

export interface StepCheck {
  word: string;
  status: StepStatus;
}

export interface LadderCheck {
  steps: StepCheck[];
  /** Whether every rung is valid and the ladder runs from start to end. */
  complete: boolean;
  /** Whether it ends at the end word. */
  reachesEnd: boolean;
}

/**
 * Check a student's ladder. `words` is the whole ladder, start word first. Each rung must
 * be a word in the list, differ from the one before it in exactly one letter, and not
 * repeat an earlier rung.
 */
export function checkLadder(
  words: readonly string[],
  index: LadderIndex,
  start: string,
  end: string
): LadderCheck {
  const seen = new Set<string>();
  const steps: StepCheck[] = words.map((word, i) => {
    let status: StepStatus = "ok";
    if (!index.words.has(word)) {
      status = "not-a-word";
    } else if (seen.has(word)) {
      status = "repeated";
    } else if (i > 0 && changedPosition(words[i - 1] as string, word) === -1) {
      status = "bad-change";
    }
    seen.add(word);
    return { word, status };
  });
  const reachesEnd = words[0] === start && words.at(-1) === end;
  return {
    steps,
    reachesEnd,
    complete: reachesEnd && steps.every((step) => step.status === "ok"),
  };
}

export interface LadderSearch {
  /** The ladder to print. */
  ladder: Ladder | null;
  /** The shortest ladder through every ENABLE word, with how many there are. */
  shortest: ShortestLadder | null;
  /**
   * Rungs of the printed ladder that are not everyday words (the end words excepted).
   * Empty when everyday words were not asked for or there is no list for this length.
   */
  unfamiliar: string[];
  /** Whether the printed ladder was chosen to favour everyday words. */
  favoursEveryday: boolean;
}

/**
 * Find the ladder to print. With `everyday` (and an everyday list for the length) the
 * ladder favours common words and lists any unfamiliar rung it still needs; otherwise it
 * is the plain shortest ladder. The shortest ladder through the whole ENABLE list is
 * always reported as well, so a longer everyday ladder is never passed off as the
 * shortest possible one.
 */
export function findLadder(
  allWords: readonly string[],
  start: string,
  end: string,
  options: { everyday: boolean; banned?: ReadonlySet<string> }
): LadderSearch {
  const banned = options.banned ?? new Set<string>();
  const index = ladderIndexFor(allWords, start.length);
  const shortest = shortestLadder(index, start, end, banned);
  if (!(options.everyday && hasEverydayWords(start.length))) {
    return {
      ladder: shortest,
      shortest,
      unfamiliar: [],
      favoursEveryday: false,
    };
  }
  const familiar = new Set(EVERYDAY_WORDS[start.length] ?? []);
  const ladder = everydayLadder(index, familiar, start, end, banned);
  const unfamiliar = (ladder?.words ?? [])
    .slice(1, -1)
    .filter((word) => !familiar.has(word));
  return { ladder, shortest, unfamiliar, favoursEveryday: true };
}

export interface PairRequest {
  length: number;
  steps: number;
}

/**
 * A random start and end word that are exactly `steps` apart through everyday words.
 * Picks a random everyday start, maps every everyday word's distance from it, and takes a
 * random word at the requested distance. Returns null when the length has no everyday
 * list or no pair that far apart turns up.
 */
export function suggestPair(
  allWords: readonly string[],
  request: PairRequest,
  rng: Rng
): { start: string; end: string } | null {
  const list = EVERYDAY_WORDS[request.length];
  if (!list) {
    return null;
  }
  const valid = new Set(
    allWords.filter((word) => word.length === request.length)
  );
  const index = buildLadderIndex(
    list.filter((word) => valid.has(word)),
    request.length
  );
  const starts = shuffled(rng, [...index.words]);
  for (const start of starts.slice(0, SUGGEST_STARTS)) {
    const reach = distancesFrom(index, start, request.steps);
    const far = reach.filter(([, distance]) => distance === request.steps);
    if (far.length > 0) {
      const [end] = far[randInt(rng, 0, far.length - 1)] as [string, number];
      return { start, end };
    }
  }
  return null;
}

const SUGGEST_STARTS = 40;

/** Every word within `limit` steps of `start`, with its distance. */
function distancesFrom(
  index: LadderIndex,
  start: string,
  limit: number
): [string, number][] {
  const distance = new Map<string, number>([[start, 0]]);
  const queue = [start];
  for (const word of queue) {
    const here = distance.get(word) ?? 0;
    if (here >= limit) {
      continue;
    }
    for (const next of neighbours(index, word)) {
      if (!distance.has(next)) {
        distance.set(next, here + 1);
        queue.push(next);
      }
    }
  }
  return [...distance.entries()];
}
